import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  borrarMascota,
  borrarVisita,
  cancelarRecordatorio,
  completarRecordatorio,
  listarAccesosMascota,
  listarAdjuntos,
  listarRecetas,
  listarRecordatorios,
  listarVisitas,
  obtenerCuentaActual,
  obtenerMascota,
  obtenerPerfilTutorLocal,
  posponerRecordatorio,
  TipoItem,
  type Adjunto,
} from "../api";
import { formatearBytes, formatearFecha } from "../formato";
import AvatarMascota from "../componentes/AvatarMascota";
import GaleriaAdjuntos from "../componentes/GaleriaAdjuntos";
import CompartirMascotaModal from "../componentes/CompartirMascotaModal";
import GraficoPeso from "../componentes/GraficoPeso";
import EmisorRecetaModal from "../componentes/EmisorRecetaModal";
import VisitaDetalleModal from "../componentes/VisitaDetalleModal";
import VisorAdjunto from "../componentes/VisorAdjunto";

const SEXO = ["Desconocido", "Macho", "Hembra"];
const ESTADO = ["", "Activa", "Fallecida", "Perdida", "Entregada"];

const ICONOS_ITEM: Record<number, string> = {
  [TipoItem.Vacuna]: "💉",
  [TipoItem.AntiparasitarioInterno]: "🪱",
  [TipoItem.AntiparasitarioExterno]: "🧴",
  [TipoItem.Medicamento]: "💊",
  [TipoItem.Procedimiento]: "🩺",
  [TipoItem.Examen]: "🔬",
  [TipoItem.Observacion]: "📝",
};

const ETIQUETAS_TIPO_ITEM: Record<number, string> = {
  [TipoItem.Vacuna]: "Vacuna",
  [TipoItem.AntiparasitarioInterno]: "Antiparasitario interno",
  [TipoItem.AntiparasitarioExterno]: "Antiparasitario externo",
  [TipoItem.Medicamento]: "Medicamento",
  [TipoItem.Procedimiento]: "Procedimiento",
  [TipoItem.Examen]: "Examen",
  [TipoItem.Observacion]: "Observación",
};

export default function MascotaDetalle() {
  const { mascotaID } = useParams();
  const navegar = useNavigate();
  const qc = useQueryClient();

  type TabFicha = "visitas" | "salud" | "fotos" | "recetas" | "tutor";
  const [searchParams, setSearchParams] = useSearchParams();

  const tabValida = (t: string | null): t is TabFicha =>
    t === "visitas" || t === "salud" || t === "fotos" || t === "recetas" || t === "tutor";

  const paramTab = searchParams.get("tab");
  const tabActiva: TabFicha = tabValida(paramTab) ? paramTab : "visitas";

  const cambiarTab = (nuevaTab: TabFicha) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (nuevaTab === "visitas") {
          next.delete("tab");
        } else {
          next.set("tab", nuevaTab);
        }
        return next;
      },
      { replace: true }
    );
  };
  const [visitaModalID, setVisitaModalID] = useState<string | null>(null);
  const [adjuntoSeleccionado, setAdjuntoSeleccionado] = useState<Adjunto | null>(null);

  const [confirmandoBorrarMascota, setConfirmandoBorrarMascota] = useState(false);
  const [visitaParaBorrar, setVisitaParaBorrar] = useState<string | null>(null);
  const [posponiendoRecID, setPosponiendoRecID] = useState<number | null>(null);
  const [nuevaFechaPosponer, setNuevaFechaPosponer] = useState<string>("");
  const [modalCompartirAbierto, setModalCompartirAbierto] = useState(false);
  const [filtroTipoVisita, setFiltroTipoVisita] = useState<string>("todos");
  const [modalRecetaAbierto, setModalRecetaAbierto] = useState(false);
  const [recetaVisitaSeleccionada, setRecetaVisitaSeleccionada] = useState<{ visitaID?: string; diagnostico?: string } | null>(null);

  const mascota = useQuery({
    queryKey: ["mascota", mascotaID],
    queryFn: () => obtenerMascota(mascotaID!),
  });

  const visitas = useQuery({
    queryKey: ["visitas", mascotaID],
    queryFn: () => listarVisitas(mascotaID!),
    enabled: Boolean(mascotaID),
  });

  const recordatorios = useQuery({
    queryKey: ["recordatorios", mascotaID],
    queryFn: () => listarRecordatorios(mascotaID!),
    enabled: Boolean(mascotaID),
  });

  const recetas = useQuery({
    queryKey: ["recetas", mascotaID],
    queryFn: () => listarRecetas(mascotaID!),
    enabled: Boolean(mascotaID),
  });

  const adjuntosQuery = useQuery({
    queryKey: ["adjuntos", mascotaID],
    queryFn: () => listarAdjuntos(mascotaID!),
    enabled: Boolean(mascotaID),
  });

  const accesos = useQuery({
    queryKey: ["accesos", mascotaID],
    queryFn: () => listarAccesosMascota(mascotaID!),
    enabled: Boolean(mascotaID),
  });

  const cuenta = useQuery({
    queryKey: ["cuenta"],
    queryFn: obtenerCuentaActual,
  });

  const adjuntosPorVisita = useMemo(() => {
    const mapa: Record<string, Adjunto[]> = {};
    for (const a of adjuntosQuery.data ?? []) {
      if (a.visitaID) {
        const lista = mapa[a.visitaID] ?? [];
        lista.push(a);
        mapa[a.visitaID] = lista;
      }
    }
    return mapa;
  }, [adjuntosQuery.data]);

  const mutacionBorrarMascota = useMutation({
    mutationFn: () => borrarMascota(mascotaID!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mascotas"] });
      navegar("/");
    },
  });

  const mutacionBorrarVisita = useMutation({
    mutationFn: (visitaID: string) => borrarVisita(visitaID),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["visitas", mascotaID] });
      qc.invalidateQueries({ queryKey: ["mascota", mascotaID] });
      qc.invalidateQueries({ queryKey: ["mascotas"] });
      qc.invalidateQueries({ queryKey: ["recordatorios", mascotaID] });
      qc.invalidateQueries({ queryKey: ["adjuntos", mascotaID] });
      setVisitaParaBorrar(null);
    },
  });

  const mutacionCompletarRec = useMutation({
    mutationFn: (recID: number) => completarRecordatorio(recID),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recordatorios", mascotaID] });
      qc.invalidateQueries({ queryKey: ["mascotas"] });
    },
  });

  const mutacionPosponerRec = useMutation({
    mutationFn: ({ recID, fecha }: { recID: number; fecha: string }) =>
      posponerRecordatorio(recID, fecha),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recordatorios", mascotaID] });
      qc.invalidateQueries({ queryKey: ["mascotas"] });
      setPosponiendoRecID(null);
      setNuevaFechaPosponer("");
    },
  });

  const mutacionCancelarRec = useMutation({
    mutationFn: (recID: number) => cancelarRecordatorio(recID),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recordatorios", mascotaID] });
      qc.invalidateQueries({ queryKey: ["mascotas"] });
    },
  });

  if (mascota.isPending) return <p className="muted">Cargando…</p>;
  if (mascota.error) return <p className="err">{mascota.error.message}</p>;

  const m = mascota.data;
  const recsPendientes = (recordatorios.data ?? []).filter((r) => r.estado === 1);
  const recsAtrasados = recsPendientes.filter((r) => r.estadoSemaforo === "Vencido");
  const todasVisitas = visitas.data ?? [];
  const tiposVisita = Array.from(new Set(todasVisitas.map((v) => v.tipoVisitaNombre))).sort();
  const visitasFiltradas =
    filtroTipoVisita === "todos"
      ? todasVisitas
      : todasVisitas.filter((v) => v.tipoVisitaNombre === filtroTipoVisita);

  const primeraVisita = visitasFiltradas.length > 0 ? visitasFiltradas[0] : null;
  const visitasAnteriores = visitasFiltradas.length > 1 ? visitasFiltradas.slice(1) : [];

  const duenoAcceso = accesos.data?.find((a) => a.esDueno || a.rol === 1);
  const tutorUsuarioID = m.esDueno ? (cuenta.data?.usuarioID ?? "") : (duenoAcceso?.usuarioID ?? "");
  const tutorNombreFallback = m.esDueno
    ? cuenta.data?.nombre || "Tutor"
    : duenoAcceso?.nombreCompleto || "Tutor responsable";
  const tutorEmailFallback = m.esDueno ? cuenta.data?.email || "" : duenoAcceso?.email || "";
  const perfilTutor = obtenerPerfilTutorLocal(
    tutorUsuarioID,
    tutorNombreFallback,
    tutorEmailFallback,
  );

  return (
    <>
      <p className="migas">
        <Link to="/">← Mis mascotas</Link>
      </p>

      {/* Cabecera Principal de la Mascota */}
      <div className="mascota-cabecera-perfil">
        <AvatarMascota
          nombre={m.nombre}
          especie={m.especie}
          fotoPerfilUrl={m.fotoPerfilUrl}
          tamano="xl"
          editable={m.puedeEditar}
          mascotaID={m.mascotaID}
        />
        <div style={{ flex: 1 }}>
          <div className="titulo-fila">
            <h1>{m.nombre}</h1>
            <div className="acciones">
              <Link
                to={`/mascotas/${m.mascotaID}/carnet`}
                className="boton"
                title="Ver e imprimir Carnet de Vacunación Oficial y Ficha de Emergencia"
              >
                🩺 Carnet de Vacunación
              </Link>
              {m.puedeEditar && (
                <>
                  <Link className="boton-primario" to={`/mascotas/${m.mascotaID}/visitas/nueva`}>
                    + Nueva visita
                  </Link>
                  <button
                    type="button"
                    className="boton"
                    onClick={() => {
                      setRecetaVisitaSeleccionada(null);
                      setModalRecetaAbierto(true);
                    }}
                    title="Emitir receta médica oficial para esta mascota"
                  >
                    📝 Emitir receta
                  </button>
                  <Link className="boton" to={`/mascotas/${m.mascotaID}/editar`}>
                    Editar datos
                  </Link>
                </>
              )}
              {m.esDueno && (
                <button
                  type="button"
                  className="boton"
                  onClick={() => setModalCompartirAbierto(true)}
                  title="Compartir ficha clínica mediante código QR o enlace seguro"
                >
                  🔗 Compartir
                </button>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap", margin: "0.25rem 0 0" }}>
            <p className="muted" style={{ margin: 0 }}>
              {m.especie} {m.raza ? `· ${m.raza}` : ""} {m.color ? `· ${m.color}` : ""} {m.edad ? `· ${m.edad}` : ""}
            </p>
            {!m.esDueno && (
              <span className="etiqueta neutra" style={{ fontSize: "0.75rem" }}>
                Accediendo como {m.rol} {m.puedeEditar ? "(edición)" : "(sólo lectura)"}
              </span>
            )}
          </div>

          <div className="mascota-badges-cabecera">
            {m.pesoKg != null && (
              <button
                type="button"
                className="badge-cabecera"
                title="Ver evolución y gráfico de peso (ir a pestaña Salud y Peso)"
                onClick={() => cambiarTab("salud")}
              >
                ⚖️ {m.pesoKg.toFixed(1)} kg
              </button>
            )}
            {m.numeroChip && (
              <button
                type="button"
                className="badge-cabecera"
                title="Ver ficha de filiación (ir a pestaña Datos y Tutor)"
                onClick={() => cambiarTab("tutor")}
              >
                🏷️ Chip: {m.numeroChip}
              </button>
            )}
            {m.esterilizada !== null && (
              <span className="badge-cabecera" title="Estado reproductivo">
                {m.esterilizada ? "✂️ Esterilizada" : "✂️ Sin esterilizar"}
              </span>
            )}
            {m.alergias && (
              <button
                type="button"
                className="badge-cabecera badge-alerta"
                title="Ver detalle de alergias (ir a pestaña Datos y Tutor)"
                onClick={() => cambiarTab("tutor")}
              >
                ⚠️ Alergias
              </button>
            )}
          </div>
        </div>
      </div>

      {m.estado !== 1 && (
        <p className="aviso">
          Esta ficha está marcada como <strong>{ESTADO[m.estado]}</strong>.
          {m.fechaFallecimiento && ` Fecha: ${formatearFecha(m.fechaFallecimiento)}.`}
        </p>
      )}

      {/* Banner de alertas para ítems pendientes atrasados */}
      {m.puedeEditar && recsAtrasados.length > 0 && (
        <div className="banner-items-atrasados" role="alert">
          <div className="banner-items-atrasados-info">
            <span className="banner-items-icono" aria-hidden="true">
              ⚠️
            </span>
            <div>
              <strong>
                {recsAtrasados.length === 1
                  ? `${m.nombre} tiene 1 aplicación o control atrasado:`
                  : `${m.nombre} tiene ${recsAtrasados.length} aplicaciones o controles atrasados:`}
              </strong>
              <div className="banner-items-botones">
                {recsAtrasados.map((rec) => (
                  <Link
                    key={rec.recordatorioID}
                    to={`/mascotas/${m.mascotaID}/visitas/nueva?productoNombre=${encodeURIComponent(rec.titulo)}&motivo=${encodeURIComponent(`Aplicación de ${rec.titulo}`)}`}
                    className="boton-chico boton-alerta-aplicar"
                    title={`Registrar aplicación para ${rec.titulo}`}
                  >
                    ⚡ Aplicar {rec.titulo}
                    <span className="banner-item-vencimiento">
                      (vencido {formatearFecha(rec.fechaProgramada)})
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Barra de Pestañas (Tabs) */}
      <div className="tabs-navegacion" role="tablist" aria-label="Secciones de la ficha clínica">
        <button
          type="button"
          role="tab"
          id="tab-visitas"
          aria-controls="panel-visitas"
          aria-selected={tabActiva === "visitas"}
          className={`tab-boton ${tabActiva === "visitas" ? "activo" : ""}`}
          onClick={() => cambiarTab("visitas")}
        >
          <span>🩺 Historial Clínico</span>
          {todasVisitas.length > 0 && <span className="tab-badge">{todasVisitas.length}</span>}
        </button>

        <button
          type="button"
          role="tab"
          id="tab-salud"
          aria-controls="panel-salud"
          aria-selected={tabActiva === "salud"}
          className={`tab-boton ${tabActiva === "salud" ? "activo" : ""}`}
          onClick={() => cambiarTab("salud")}
        >
          <span>⚖️ Salud y Peso</span>
          {recsPendientes.length > 0 && (
            <span className="tab-badge alerta">{recsPendientes.length} pend.</span>
          )}
        </button>

        <button
          type="button"
          role="tab"
          id="tab-fotos"
          aria-controls="panel-fotos"
          aria-selected={tabActiva === "fotos"}
          className={`tab-boton ${tabActiva === "fotos" ? "activo" : ""}`}
          onClick={() => cambiarTab("fotos")}
        >
          <span>📷 Fotos y Documentos</span>
          {(adjuntosQuery.data?.length ?? 0) > 0 && (
            <span className="tab-badge">{adjuntosQuery.data!.length}</span>
          )}
        </button>

        <button
          type="button"
          role="tab"
          id="tab-recetas"
          aria-controls="panel-recetas"
          aria-selected={tabActiva === "recetas"}
          className={`tab-boton ${tabActiva === "recetas" ? "activo" : ""}`}
          onClick={() => cambiarTab("recetas")}
        >
          <span>📝 Recetas</span>
          {(recetas.data?.length ?? 0) > 0 && (
            <span className="tab-badge">{recetas.data!.length}</span>
          )}
        </button>

        <button
          type="button"
          role="tab"
          id="tab-tutor"
          aria-controls="panel-tutor"
          aria-selected={tabActiva === "tutor"}
          className={`tab-boton ${tabActiva === "tutor" ? "activo" : ""}`}
          onClick={() => cambiarTab("tutor")}
        >
          <span>👤 Datos y Tutor</span>
        </button>
      </div>

      {/* PESTAÑA 1: HISTORIAL CLÍNICO Y VISITAS */}
      {tabActiva === "visitas" && (
        <section id="panel-visitas" role="tabpanel" aria-labelledby="tab-visitas" className="panel-tab-contenido">
          <div className="titulo-fila">
            <div>
              <h2>Historial clínico y atenciones</h2>
              {todasVisitas.length > 0 && (
                <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
                  {todasVisitas.length === 1
                    ? "1 atención registrada"
                    : `${todasVisitas.length} atenciones registradas en total`}
                </p>
              )}
            </div>
            {m.puedeEditar && (
              <Link className="boton-primario boton-chico" to={`/mascotas/${m.mascotaID}/visitas/nueva`}>
                + Registrar visita
              </Link>
            )}
          </div>

          {/* Filtros por tipo de visita */}
          {todasVisitas.length > 0 && tiposVisita.length > 1 && (
            <div className="filtros-visitas-contenedor">
              <span className="filtro-etiqueta-texto">Filtrar:</span>
              <div className="filtros-visitas-pills">
                <button
                  type="button"
                  className={`filtro-pill ${filtroTipoVisita === "todos" ? "activo" : ""}`}
                  onClick={() => setFiltroTipoVisita("todos")}
                >
                  Todas ({todasVisitas.length})
                </button>
                {tiposVisita.map((tipo) => {
                  const cant = todasVisitas.filter((v) => v.tipoVisitaNombre === tipo).length;
                  return (
                    <button
                      key={tipo}
                      type="button"
                      className={`filtro-pill ${filtroTipoVisita === tipo ? "activo" : ""}`}
                      onClick={() => setFiltroTipoVisita(tipo)}
                    >
                      {tipo} ({cant})
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {visitas.isPending && <p className="muted">Cargando historial de visitas…</p>}
          {visitas.error && <p className="err">{visitas.error.message}</p>}

          {visitas.data && visitas.data.length === 0 && (
            <div className="vacio" style={{ padding: "2.5rem 1rem" }}>
              <span style={{ fontSize: "2.5rem" }}>🩺</span>
              <p className="muted" style={{ marginTop: "0.5rem" }}>Aún no hay visitas registradas para {m.nombre}.</p>
              {m.puedeEditar && (
                <Link
                  className="boton-primario"
                  to={`/mascotas/${m.mascotaID}/visitas/nueva`}
                  style={{ marginTop: "0.5rem" }}
                >
                  Registrar primera atención
                </Link>
              )}
            </div>
          )}

          {visitas.data && visitas.data.length > 0 && visitasFiltradas.length === 0 && (
            <div className="vacio" style={{ padding: "1.5rem 1rem" }}>
              <p className="muted">No hay visitas registradas del tipo "{filtroTipoVisita}".</p>
              <button
                type="button"
                className="boton-chico"
                onClick={() => setFiltroTipoVisita("todos")}
                style={{ marginTop: "0.5rem" }}
              >
                Mostrar todas las visitas
              </button>
            </div>
          )}

          {/* ÚLTIMA VISITA DESTACADA CON LUJO DE DETALLE */}
          {primeraVisita && (
            <div className="bloque-ultima-visita" style={{ marginTop: "1rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                <span className="etiqueta exito" style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  ⭐ Atención más reciente
                </span>
                <span className="muted" style={{ fontSize: "0.8rem" }}>
                  {formatearFecha(primeraVisita.fecha)}
                </span>
              </div>

              <article className="visita-card visita-destacada panel">
                <header className="visita-cabecera">
                  <div className="visita-cabecera-datos">
                    <span className="visita-fecha">{formatearFecha(primeraVisita.fecha)}</span>
                    <span className="etiqueta neutra">{primeraVisita.tipoVisitaNombre}</span>
                    {primeraVisita.pesoKg != null && (
                      <span className="etiqueta neutra">⚖️ {primeraVisita.pesoKg.toFixed(1)} kg</span>
                    )}
                    {primeraVisita.clinicaNombre && (
                      <span className="muted" style={{ fontSize: "0.85rem" }}>
                        🏥 {primeraVisita.clinicaNombre}
                      </span>
                    )}
                    {primeraVisita.profesionalNombre && (
                      <span className="muted" style={{ fontSize: "0.85rem" }}>
                        👨‍⚕️ {primeraVisita.profesionalNombre}
                      </span>
                    )}
                  </div>

                  {m.puedeEditar && (
                    <div className="visita-acciones">
                      <Link
                        className="boton-chico"
                        to={`/visitas/${primeraVisita.visitaID}/editar`}
                        title="Editar esta visita"
                      >
                        Editar
                      </Link>
                      <button
                        type="button"
                        className="boton-chico"
                        onClick={() => {
                          setRecetaVisitaSeleccionada({
                            visitaID: primeraVisita.visitaID,
                            diagnostico: primeraVisita.diagnostico ?? undefined,
                          });
                          setModalRecetaAbierto(true);
                        }}
                        title="Emitir receta médica oficial para esta atención"
                      >
                        📝 Receta
                      </button>
                      {visitaParaBorrar === primeraVisita.visitaID ? (
                        <div className="confirmar-borrar-inline">
                          <button
                            type="button"
                            className="boton-peligro boton-chico"
                            disabled={mutacionBorrarVisita.isPending}
                            onClick={() => mutacionBorrarVisita.mutate(primeraVisita.visitaID)}
                          >
                            Confirmar
                          </button>
                          <button
                            type="button"
                            className="boton-chico"
                            onClick={() => setVisitaParaBorrar(null)}
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="boton-chico"
                          onClick={() => setVisitaParaBorrar(primeraVisita.visitaID)}
                          title="Eliminar visita"
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  )}
                </header>

                {/* Motivo y Diagnóstico */}
                {(primeraVisita.motivo || primeraVisita.diagnostico) && (
                  <div className="visita-evaluacion">
                    {primeraVisita.motivo && (
                      <p className="visita-motivo">
                        <strong>Motivo:</strong> {primeraVisita.motivo}
                      </p>
                    )}
                    {primeraVisita.diagnostico && (
                      <p className="visita-diagnostico">
                        <strong>Diagnóstico / Hallazgos:</strong> {primeraVisita.diagnostico}
                      </p>
                    )}
                  </div>
                )}

                {/* Items administrados */}
                {primeraVisita.items && primeraVisita.items.length > 0 && (
                  <div className="visita-items">
                    <h3 style={{ fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--ink-3)", marginBottom: "0.4rem" }}>
                      Acciones realizadas en esta visita ({primeraVisita.items.length}):
                    </h3>
                    <ul className="visita-items-lista">
                      {primeraVisita.items.map((it) => (
                        <li key={it.visitaItemID} className="visita-item-fila">
                          <span className="visita-item-icono" title={ETIQUETAS_TIPO_ITEM[it.tipo] || "Ítem"}>
                            {ICONOS_ITEM[it.tipo] || "📌"}
                          </span>
                          <div className="visita-item-info">
                            <div className="visita-item-nombre">
                              <strong>{it.productoNombre || it.nombreLibre || it.descripcion}</strong>
                              <span className="etiqueta neutra" style={{ fontSize: "0.75rem", marginLeft: "0.5rem" }}>
                                {ETIQUETAS_TIPO_ITEM[it.tipo] || "Ítem"}
                              </span>
                            </div>
                            {(it.dosis || it.via || it.lote) && (
                              <div className="visita-item-detalles muted">
                                {it.dosis && <span>Dosis: {it.dosis}</span>}
                                {it.via && <span> · Vía: {it.via}</span>}
                                {it.lote && <span> · Lote: {it.lote}</span>}
                              </div>
                            )}
                            {it.notas && <p className="visita-item-notas">{it.notas}</p>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Notas generales de la visita */}
                {primeraVisita.notas && (
                  <div className="visita-notas-pie">
                    <p style={{ margin: 0 }}>
                      <strong>Indicaciones:</strong> {primeraVisita.notas}
                    </p>
                  </div>
                )}

                {/* Mini-galería de fotos de la visita */}
                {(() => {
                  const fotosPrimeraVisita = adjuntosPorVisita[primeraVisita.visitaID] ?? [];
                  if (fotosPrimeraVisita.length === 0) return null;
                  return (
                    <div style={{ marginTop: "0.75rem", borderTop: "1px dashed var(--rule)", paddingTop: "0.75rem" }}>
                      <h3 style={{ fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--ink-3)", marginBottom: "0.4rem" }}>
                        Fotos y documentos de esta atención ({fotosPrimeraVisita.length}):
                      </h3>
                      <div className="mini-galeria-adjuntos">
                        {fotosPrimeraVisita.map((adj) => {
                          const esImg = adj.contentType.startsWith("image/");
                          return (
                            <div
                              key={adj.adjuntoID}
                              className="mini-adjunto-tarjeta"
                              onClick={() => setAdjuntoSeleccionado(adj)}
                              title="Clic para ampliar en visor"
                            >
                              {esImg ? (
                                <img src={adj.thumbnailUrl || adj.url} alt="Foto de la visita" className="mini-adjunto-img" loading="lazy" />
                              ) : (
                                <div className="mini-adjunto-doc">
                                  <span>📄</span>
                                  <small>PDF</small>
                                </div>
                              )}
                              <div className="mini-adjunto-meta">
                                <span>{formatearBytes(adj.bytes)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </article>
            </div>
          )}

          {/* TABLA DE ATENCIONES ANTERIORES */}
          {visitasAnteriores.length > 0 && (
            <div style={{ marginTop: "2rem" }}>
              <div className="titulo-fila" style={{ marginBottom: "0.75rem" }}>
                <h3 style={{ fontSize: "1.1rem", margin: 0 }}>
                  Atenciones anteriores ({visitasAnteriores.length})
                </h3>
                <span className="muted" style={{ fontSize: "0.8rem" }}>
                  Haz clic en cualquier fila para abrir el expediente completo
                </span>
              </div>

              <div className="tabla-visitas-contenedor panel">
                <table className="tabla-visitas">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Tipo</th>
                      <th>Motivo / Diagnóstico</th>
                      <th>Profesional / Clínica</th>
                      <th>Ítems</th>
                      <th>Fotos</th>
                      <th style={{ textAlign: "right" }}>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visitasAnteriores.map((v) => {
                      const fotosVisita = adjuntosPorVisita[v.visitaID] ?? [];
                      return (
                        <tr
                          key={v.visitaID}
                          className="fila-visita-resumen"
                          onClick={() => setVisitaModalID(v.visitaID)}
                          title="Clic para ver detalle completo"
                        >
                          <td className="celda-fecha">
                            <strong>{formatearFecha(v.fecha)}</strong>
                          </td>
                          <td>
                            <span className="etiqueta neutra" style={{ fontSize: "0.75rem" }}>
                              {v.tipoVisitaNombre}
                            </span>
                          </td>
                          <td className="celda-motivo">
                            <div className="texto-recortado">
                              <strong>{v.motivo || "Atención clínica"}</strong>
                              {v.diagnostico && <span className="muted"> · {v.diagnostico}</span>}
                            </div>
                          </td>
                          <td className="celda-profesional muted">
                            {v.profesionalNombre || v.clinicaNombre || "—"}
                          </td>
                          <td>
                            {v.items && v.items.length > 0 ? (
                              <span
                                className="etiqueta neutra"
                                style={{ fontSize: "0.75rem" }}
                                title={v.items.map((i) => i.productoNombre || i.nombreLibre).join(", ")}
                              >
                                {v.items.length} {v.items.length === 1 ? "ítem" : "ítems"}
                              </span>
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </td>
                          <td>
                            {fotosVisita.length > 0 ? (
                              <span
                                className="etiqueta neutra"
                                style={{ fontSize: "0.75rem" }}
                                title={`${fotosVisita.length} archivo(s)`}
                              >
                                📷 {fotosVisita.length}
                              </span>
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </td>
                          <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className="boton-chico"
                              onClick={() => setVisitaModalID(v.visitaID)}
                            >
                              Ver detalle
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}

      {/* PESTAÑA 2: SALUD PREVENTIVA Y PESO */}
      {tabActiva === "salud" && (
        <section id="panel-salud" role="tabpanel" aria-labelledby="tab-salud" className="panel-tab-contenido" style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          {/* Próximos recordatorios */}
          <section className="panel">
            <div className="titulo-fila">
              <div>
                <h2>Próximos recordatorios y controles</h2>
                <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
                  Vacunaciones, desparasitaciones y controles agendados.
                </p>
              </div>
            </div>

            {recsPendientes.length === 0 ? (
              <div style={{ padding: "1.25rem 0", textAlign: "center" }} className="muted">
                <p style={{ margin: 0 }}>🎉 No hay controles ni vacunas pendientes para {m.nombre}.</p>
                <p style={{ margin: "0.25rem 0 0", fontSize: "0.85rem" }}>Se agendan solos al registrar cada visita o vacuna.</p>
              </div>
            ) : (
              <ul className="recordatorios" style={{ marginTop: "1rem" }}>
                {recsPendientes.map((rec) => {
                  const claseSemaforo =
                    rec.estadoSemaforo === "Vencido"
                      ? "vencido"
                      : rec.estadoSemaforo === "PorVencer"
                        ? "por-vencer"
                        : "al-dia";

                  return (
                    <li key={rec.recordatorioID} className={`recordatorio ${claseSemaforo}`}>
                      {m.puedeEditar ? (
                        <Link
                          to={`/mascotas/${m.mascotaID}/visitas/nueva?productoNombre=${encodeURIComponent(rec.titulo)}&motivo=${encodeURIComponent(`Aplicación de ${rec.titulo}`)}`}
                          className="rec-info rec-info-clickeable"
                          title={`Hacer clic para registrar aplicación de ${rec.titulo}`}
                        >
                          <span className="rec-titulo">
                            <strong>{rec.titulo}</strong>
                          </span>
                          <span className="rec-fecha">
                            {formatearFecha(rec.fechaProgramada)}
                            {rec.estadoSemaforo === "Vencido" && (
                              <span className="etiqueta vencido" style={{ marginLeft: "0.5rem" }}>
                                Vencido
                              </span>
                            )}
                          </span>
                        </Link>
                      ) : (
                        <div className="rec-info">
                          <span className="rec-titulo">
                            <strong>{rec.titulo}</strong>
                          </span>
                          <span className="rec-fecha">
                            {formatearFecha(rec.fechaProgramada)}
                            {rec.estadoSemaforo === "Vencido" && (
                              <span className="etiqueta vencido" style={{ marginLeft: "0.5rem" }}>
                                Vencido
                              </span>
                            )}
                          </span>
                        </div>
                      )}

                      {m.puedeEditar && (
                        <div className="rec-acciones">
                          {posponiendoRecID === rec.recordatorioID ? (
                            <div className="rec-posponer-form">
                              <input
                                type="date"
                                value={nuevaFechaPosponer}
                                onChange={(e) => setNuevaFechaPosponer(e.target.value)}
                              />
                              <button
                                type="button"
                                className="boton-chico boton-primario"
                                disabled={!nuevaFechaPosponer || mutacionPosponerRec.isPending}
                                onClick={() =>
                                  mutacionPosponerRec.mutate({
                                    recID: rec.recordatorioID,
                                    fecha: nuevaFechaPosponer,
                                  })
                                }
                              >
                                Guardar
                              </button>
                              <button
                                type="button"
                                className="boton-chico"
                                onClick={() => setPosponiendoRecID(null)}
                              >
                                Cancelar
                              </button>
                            </div>
                          ) : (
                            <div className="acciones" style={{ gap: "0.4rem" }}>
                              <Link
                                to={`/mascotas/${m.mascotaID}/visitas/nueva?productoNombre=${encodeURIComponent(rec.titulo)}&motivo=${encodeURIComponent(`Aplicación de ${rec.titulo}`)}`}
                                className="boton-chico boton-primario"
                                title={`Registrar atención clínica para aplicar ${rec.titulo}`}
                              >
                                ⚡ Aplicar
                              </Link>
                              <button
                                type="button"
                                className="boton-chico"
                                title="Marcar solo como cumplido en este recordatorio"
                                disabled={mutacionCompletarRec.isPending}
                                onClick={() => mutacionCompletarRec.mutate(rec.recordatorioID)}
                              >
                                ✓ Cumplido
                              </button>
                              <button
                                type="button"
                                className="boton-chico"
                                title="Posponer para otra fecha"
                                onClick={() => {
                                  setPosponiendoRecID(rec.recordatorioID);
                                  setNuevaFechaPosponer(rec.fechaProgramada);
                                }}
                              >
                                📅 Posponer
                              </button>
                              <button
                                type="button"
                                className="boton-chico"
                                title="Cancelar recordatorio"
                                disabled={mutacionCancelarRec.isPending}
                                onClick={() => {
                                  if (confirm(`¿Cancelar recordatorio de ${rec.titulo}?`)) {
                                    mutacionCancelarRec.mutate(rec.recordatorioID);
                                  }
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Gráfico y Evolución de Peso */}
          <GraficoPeso
            mascotaID={m.mascotaID}
            nombreMascota={m.nombre}
            puedeEditar={m.puedeEditar}
          />
        </section>
      )}

      {/* PESTAÑA 3: FOTOS Y DOCUMENTOS */}
      {tabActiva === "fotos" && (
        <section id="panel-fotos" role="tabpanel" aria-labelledby="tab-fotos" className="panel-tab-contenido panel">
          <div className="titulo-fila">
            <div>
              <h2>Documentos, carnet y fotos</h2>
              <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
                Biblioteca multimedia de la mascota: carnet escaneado, exámenes de laboratorio, radiografías y fotos clínicas.
              </p>
            </div>
          </div>
          <GaleriaAdjuntos
            mascotaID={m.mascotaID}
            puedeEditar={m.puedeEditar}
          />
        </section>
      )}

      {/* PESTAÑA 4: RECETAS MÉDICAS */}
      {tabActiva === "recetas" && (
        <section id="panel-recetas" role="tabpanel" aria-labelledby="tab-recetas" className="panel-tab-contenido panel">
          <div className="titulo-fila">
            <div>
              <h2>Recetas Médicas e Indicaciones</h2>
              <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
                Prescripciones médicas oficiales emitidas (formato Rp. imprimible y exportable).
              </p>
            </div>
            {m.puedeEditar && (
              <button
                type="button"
                className="boton-primario boton-chico"
                onClick={() => {
                  setRecetaVisitaSeleccionada(null);
                  setModalRecetaAbierto(true);
                }}
              >
                ➕ Emitir receta médica
              </button>
            )}
          </div>

          {recetas.isPending ? (
            <p className="muted">Cargando recetas…</p>
          ) : (recetas.data ?? []).length === 0 ? (
            <div style={{ padding: "2.5rem 0", textAlign: "center" }} className="muted">
              <span style={{ fontSize: "2.5rem" }}>💊</span>
              <p style={{ margin: "0.5rem 0 0", fontSize: "0.95rem" }}>No hay recetas médicas registradas para {m.nombre}.</p>
              {m.puedeEditar && (
                <button
                  type="button"
                  className="boton"
                  style={{ marginTop: "0.75rem" }}
                  onClick={() => {
                    setRecetaVisitaSeleccionada(null);
                    setModalRecetaAbierto(true);
                  }}
                >
                  📝 Emitir la primera receta
                </button>
              )}
            </div>
          ) : (
            <div
              className="recetas-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "1rem",
                marginTop: "1rem",
              }}
            >
              {recetas.data!.map((r) => (
                <article
                  key={r.recetaID}
                  className="tarjeta-receta-item"
                  style={{
                    border: "1px solid var(--borde)",
                    borderRadius: "0.5rem",
                    padding: "1rem",
                    backgroundColor: "var(--sunken)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.4rem" }}>
                      <span style={{ fontSize: "0.8rem", color: "var(--ink-2)", fontWeight: 600 }}>
                        📅 {formatearFecha(r.fechaEmision)}
                      </span>
                      <span className="etiqueta neutra" style={{ fontSize: "0.75rem" }}>
                        {r.cantidadItems} {r.cantidadItems === 1 ? "fármaco" : "fármacos"}
                      </span>
                    </div>

                    <h3 style={{ margin: "0.2rem 0 0.4rem", fontSize: "1rem", color: "var(--ink-1)" }}>
                      {r.diagnostico || "Prescripción médica"}
                    </h3>

                    {r.profesionalNombre && (
                      <p className="muted" style={{ margin: "0.2rem 0", fontSize: "0.82rem" }}>
                        👨‍⚕️ {r.profesionalNombre}
                      </p>
                    )}

                    {r.clinicaNombre && (
                      <p className="muted" style={{ margin: "0.1rem 0 0.4rem", fontSize: "0.8rem" }}>
                        🏥 {r.clinicaNombre}
                      </p>
                    )}
                  </div>

                  <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem" }}>
                    <Link
                      to={`/recetas/${r.recetaID}`}
                      className="boton-chico boton-primario"
                      style={{ flex: 1, textAlign: "center", textDecoration: "none" }}
                    >
                      🖨️ Ver / Imprimir receta
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {/* PESTAÑA 5: DATOS Y TUTOR */}
      {tabActiva === "tutor" && (
        <section id="panel-tutor" role="tabpanel" aria-labelledby="tab-tutor" className="panel-tab-contenido" style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          {/* Datos del Tutor Responsable */}
          <section className="panel">
            <div className="titulo-fila">
              <div>
                <h2>Tutor Responsable y Contacto</h2>
                <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
                  Información de contacto para urgencias clínicas, hospitalización y emisión de recetas.
                </p>
              </div>
              {m.esDueno && (
                <Link className="boton-chico" to="/perfil" title="Editar mis datos de contacto">
                  ✏️ Editar mis datos
                </Link>
              )}
            </div>

            <div
              className="tutor-card-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "1rem",
                marginTop: "0.75rem",
              }}
            >
              <div
                className="tutor-info-bloque"
                style={{
                  padding: "0.75rem",
                  backgroundColor: "var(--sunken)",
                  borderRadius: "0.5rem",
                }}
              >
                <span
                  className="muted"
                  style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em" }}
                >
                  Tutor / Dueño
                </span>
                <p style={{ margin: "0.2rem 0 0", fontWeight: 600, fontSize: "1rem" }}>
                  👤 {perfilTutor.nombreCompleto}
                </p>
                {perfilTutor.rut && (
                  <span className="muted" style={{ fontSize: "0.8rem", display: "block" }}>
                    RUT: {perfilTutor.rut}
                  </span>
                )}
                {perfilTutor.email && (
                  <p style={{ margin: "0.2rem 0 0", fontSize: "0.85rem", color: "var(--ink-2)" }}>
                    📧 {perfilTutor.email}
                  </p>
                )}
              </div>

              <div
                className="tutor-info-bloque"
                style={{
                  padding: "0.75rem",
                  backgroundColor: "var(--sunken)",
                  borderRadius: "0.5rem",
                }}
              >
                <span
                  className="muted"
                  style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em" }}
                >
                  Contacto Telefónico
                </span>
                {perfilTutor.telefono ? (
                  <div style={{ marginTop: "0.25rem", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                    <a
                      href={`tel:${perfilTutor.telefono.replace(/\s+/g, "")}`}
                      style={{
                        fontWeight: 600,
                        color: "var(--accent)",
                        textDecoration: "none",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.3rem",
                      }}
                    >
                      📞 {perfilTutor.telefono}
                    </a>
                    <a
                      href={`https://wa.me/${perfilTutor.telefono.replace(/[^0-9]/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="etiqueta exito"
                      style={{
                        display: "inline-flex",
                        width: "fit-content",
                        textDecoration: "none",
                        fontSize: "0.75rem",
                        padding: "0.2rem 0.5rem",
                      }}
                    >
                      💬 Enviar WhatsApp
                    </a>
                  </div>
                ) : (
                  <p className="muted" style={{ margin: "0.25rem 0 0", fontSize: "0.85rem" }}>
                    Sin teléfono registrado
                  </p>
                )}
              </div>

              {(perfilTutor.telefonoEmergencia || perfilTutor.direccion || perfilTutor.comuna) && (
                <div
                  className="tutor-info-bloque"
                  style={{
                    padding: "0.75rem",
                    backgroundColor: "var(--sunken)",
                    borderRadius: "0.5rem",
                  }}
                >
                  <span
                    className="muted"
                    style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em" }}
                  >
                    Emergencia y Domicilio
                  </span>
                  {perfilTutor.telefonoEmergencia && (
                    <p style={{ margin: "0.2rem 0 0", fontSize: "0.85rem" }}>
                      🚨 <strong>Emergencia:</strong>{" "}
                      <a
                        href={`tel:${perfilTutor.telefonoEmergencia.replace(/\s+/g, "")}`}
                        style={{ color: "var(--crit)", textDecoration: "none", fontWeight: 600 }}
                      >
                        {perfilTutor.telefonoEmergencia}
                      </a>
                    </p>
                  )}
                  {(perfilTutor.direccion || perfilTutor.comuna) && (
                    <p style={{ margin: "0.2rem 0 0", fontSize: "0.85rem", color: "var(--ink-2)" }}>
                      📍 {[perfilTutor.direccion, perfilTutor.comuna].filter(Boolean).join(", ")}
                    </p>
                  )}
                </div>
              )}
            </div>

            {perfilTutor.notasContacto && (
              <p className="muted" style={{ margin: "0.75rem 0 0", fontSize: "0.85rem", fontStyle: "italic" }}>
                📌 <strong>Notas de contacto:</strong> {perfilTutor.notasContacto}
              </p>
            )}
          </section>

          {/* Datos generales de filiación de la mascota */}
          <section className="panel">
            <h2>Ficha de filiación</h2>
            <dl className="datos">
              <dt>Especie</dt>
              <dd>{m.especie}</dd>
              <dt>Raza</dt>
              <dd>{m.raza ?? "—"}</dd>
              <dt>Sexo</dt>
              <dd>{SEXO[m.sexo] ?? "—"}</dd>
              <dt>Color</dt>
              <dd>{m.color ?? "—"}</dd>
              <dt>Nacimiento</dt>
              <dd>
                {formatearFecha(m.fechaNacimiento)}
                {m.edad && <span className="muted"> · {m.edad}</span>}
              </dd>
              <dt>Lugar de nacimiento</dt>
              <dd>{m.lugarNacimiento ?? "—"}</dd>
              <dt>Último peso</dt>
              <dd>
                {m.pesoKg != null ? (
                  <>
                    {m.pesoKg.toFixed(1)} kg
                    <span className="muted"> · {formatearFecha(m.pesoFecha)}</span>
                  </>
                ) : (
                  "—"
                )}
              </dd>
              <dt>Chip</dt>
              <dd>{m.numeroChip ?? "—"}</dd>
              <dt>Esterilizada</dt>
              <dd>
                {m.esterilizada === null ? "Sin dato" : m.esterilizada ? "Sí" : "No"}
                {m.esterilizada && m.fechaEsterilizacion && (
                  <span className="muted"> · {formatearFecha(m.fechaEsterilizacion)}</span>
                )}
              </dd>
            </dl>

            {m.alergias && (
              <div className="bloque">
                <h3>Alergias</h3>
                <p>{m.alergias}</p>
              </div>
            )}

            {m.observaciones && (
              <div className="bloque">
                <h3>Observaciones</h3>
                <p>{m.observaciones}</p>
              </div>
            )}
          </section>

          {/* Zona de peligro */}
          {m.esDueno && (
            <section className="panel zona-riesgo">
              <h2>Eliminar ficha</h2>
              {!confirmandoBorrarMascota ? (
                <>
                  <p className="muted">
                    La ficha deja de aparecer en tu lista principal, pero el historial médico no se destruye.
                  </p>
                  <button className="boton-peligro" onClick={() => setConfirmandoBorrarMascota(true)}>
                    Eliminar {m.nombre}
                  </button>
                </>
              ) : (
                <>
                  <p>
                    ¿Seguro que quieres eliminar la ficha de <strong>{m.nombre}</strong>?
                  </p>
                  <div className="acciones">
                    <button
                      className="boton-peligro"
                      onClick={() => mutacionBorrarMascota.mutate()}
                      disabled={mutacionBorrarMascota.isPending}
                    >
                      {mutacionBorrarMascota.isPending ? "Eliminando…" : "Sí, eliminar"}
                    </button>
                    <button onClick={() => setConfirmandoBorrarMascota(false)}>Cancelar</button>
                  </div>
                  {mutacionBorrarMascota.error && (
                    <p className="err">{mutacionBorrarMascota.error.message}</p>
                  )}
                </>
              )}
            </section>
          )}
        </section>
      )}

      {/* MODAL DETALLE DE VISITA (CARGA ASÍNCRONA) */}
      <VisitaDetalleModal
        visitaID={visitaModalID}
        onCerrar={() => setVisitaModalID(null)}
        puedeEditar={m.puedeEditar}
        onEmitirReceta={(vID, diag) => {
          setRecetaVisitaSeleccionada({ visitaID: vID, diagnostico: diag });
          setModalRecetaAbierto(true);
        }}
      />

      {/* VISOR LIGHTBOX PARA MINIATURAS DE FOTOS */}
      <VisorAdjunto
        adjunto={adjuntoSeleccionado}
        onCerrar={() => setAdjuntoSeleccionado(null)}
        puedeEditar={m.puedeEditar}
      />

      {/* MODAL COMPARTIR */}
      {m.esDueno && (
        <CompartirMascotaModal
          abierto={modalCompartirAbierto}
          onCerrar={() => setModalCompartirAbierto(false)}
          mascotaID={m.mascotaID}
          nombreMascota={m.nombre}
        />
      )}

      {/* MODAL EMISOR DE RECETAS */}
      {modalRecetaAbierto && (
        <EmisorRecetaModal
          abierto={modalRecetaAbierto}
          onCerrar={() => {
            setModalRecetaAbierto(false);
            setRecetaVisitaSeleccionada(null);
          }}
          mascotaID={m.mascotaID}
          nombreMascota={m.nombre}
          especieID={m.especieID}
          visitaID={recetaVisitaSeleccionada?.visitaID}
          diagnosticoVisita={recetaVisitaSeleccionada?.diagnostico}
        />
      )}
    </>
  );
}
